import React, { useState, useEffect } from "react";
import { Table, Button, Tag, Tooltip, Modal, Form, Input, Select, message, Dropdown, Tabs, Spin, Empty } from "antd";
import {
  DeleteOutlined,
  SendOutlined,
  MoreOutlined,
  PlusOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_CONST } from "../../const";

const { Option } = Select;

const InviteContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;

  .invite-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    .stats {
      display: flex;
      gap: 24px;
      
      .stat-item {
        display: flex;
        align-items: center;
        gap: 8px;
        
        .stat-count {
          font-size: 18px;
          font-weight: 600;
          color: #014c75;
        }
        
        .stat-label {
          color: #666;
          font-size: 14px;
        }
      }
    }
    
    .invite-button {
      background: #014c75;
      border-color: #014c75;
      
      &:hover {
        background: #013a5c !important;
        border-color: #013a5c !important;
      }
    }
  }
  
  .role-tag {
    &.global {
      background: #e6f7ff;
      border-color: #91d5ff;
      color: #1890ff;
    }
    &.organization {
      background: #f6ffed;
      border-color: #b7eb8f;
      color: #52c41a;
    }
  }
`;

const InviteUsersTab = ({ role = "admin", organizationId, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [userOrgId, setUserOrgId] = useState(organizationId);
  const [parentRole, setParentRole] = useState("Customer");
  const [orgLoading, setOrgLoading] = useState(true);
  const [noOrgFound, setNoOrgFound] = useState(false);
  const [isCurrentUserFullAccess, setIsCurrentUserFullAccess] = useState(false);
  const [changeRoleVisible, setChangeRoleVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    setNoOrgFound(false);
    fetchUserOrganization();
  }, [refreshKey]);

  useEffect(() => {
    if (userOrgId) {
      fetchMembers();
      fetchInvitations();
    }
  }, [userOrgId]);

  const fetchUserOrganization = async () => {
    setOrgLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const [orgRes, profileRes, permRes] = await Promise.all([
        axios.get(API_CONST.GET_MY_ORGANIZATION, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(API_CONST.GET_MY_PROFILE, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
        fetch(API_CONST.GET_MY_PERMISSIONS, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      ]);
      if (orgRes.data?.organization?.id) {
        setUserOrgId(orgRes.data.organization.id);
        const userRole = localStorage.getItem("userRole");
        if (userRole === "Partner") {
          setParentRole("Partner");
        } else if (userRole === "Admin") {
          setParentRole("Admin");
        } else {
          setParentRole("Customer");
        }
      } else {
        setNoOrgFound(true);
      }
      const permissions = permRes?.permissions || [];
      setIsCurrentUserFullAccess(permissions.includes("team.manage"));
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      setNoOrgFound(true);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOpenChangeRole = (member) => {
    setSelectedMember(member);
    fetchAvailableRoles();
    roleForm.setFieldsValue({ profileRoleId: member.profileRoleId || undefined });
    setChangeRoleVisible(true);
  };

  const handleSubmitChangeRole = async (values) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.UPDATE_MEMBER_ROLE}/${selectedMember.id}`,
        { profileRoleId: values.profileRoleId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Member role updated successfully");
      setChangeRoleVisible(false);
      roleForm.resetFields();
      fetchMembers();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenChangePassword = (member) => {
    setSelectedMember(member);
    passwordForm.resetFields();
    setChangePasswordVisible(true);
  };

  const handleSubmitChangePassword = async (values) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.RESET_MEMBER_PASSWORD}/${selectedMember.id}`,
        { newPassword: values.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Password reset successfully");
      setChangePasswordVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to reset password");
    } finally {
      setActionLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!userOrgId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TEAM_MEMBERS,
        { organizationId: userOrgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(response.data?.members || []);
    } catch (error) {
      console.error("Failed to fetch team members:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!userOrgId) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_INVITATIONS,
        { organizationId: userOrgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(response.data?.invitations || []);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
    }
  };

  const fetchAvailableRoles = async () => {
    if (!userOrgId) return;
    setRolesLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_AVAILABLE_ROLES}?organizationId=${userOrgId}&parentRole=${parentRole}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableRoles(response.data?.roles || []);
    } catch (error) {
      console.error("Failed to fetch available roles:", error);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleInviteUser = () => {
    fetchAvailableRoles();
    setInviteModalVisible(true);
  };

  const handleSubmitInvite = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        API_CONST.CREATE_INVITATION,
        {
          email: values.email,
          name: values.name,
          contact: values.contact,
          profileRoleId: values.profileRoleId || null,
          organizationId: userOrgId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation sent successfully!");
      setInviteModalVisible(false);
      form.resetFields();
      fetchInvitations();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.RESEND_INVITATION}/${record.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Invitation resent to ${record.email}`);
      fetchInvitations();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to resend invitation");
    }
  };

  const handleCancelInvitation = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.CANCEL_INVITATION}/${record.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation cancelled");
      fetchInvitations();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to cancel invitation");
    }
  };

  const handleDeleteInvitation = (record) => {
    Modal.confirm({
      title: "Delete Invitation",
      content: `Are you sure you want to delete the invitation for ${record.email}?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(`${API_CONST.DELETE_INVITATION}/${record.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success("Invitation deleted");
          fetchInvitations();
        } catch (error) {
          message.error(error.response?.data?.error || "Failed to delete invitation");
        }
      },
    });
  };

  const getActionMenuItems = (record) => {
    const items = [];
    if (record.status === "pending") {
      items.push({
        key: "cancel",
        label: "Cancel Invitation",
        danger: true,
      });
    }
    items.push({
      key: "delete",
      label: "Delete",
      danger: true,
    });
    return items;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
      case "accepted":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "expired":
      case "cancelled":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    }
  };

  const memberColumns = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserOutlined style={{ color: "#014c75" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Email ID",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (text) => <span style={{ color: "#014c75" }}>{text}</span>,
    },
    {
      title: "Mobile",
      dataIndex: "contact",
      key: "contact",
      width: 140,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
      render: (role, record) => (
        <Tag color="blue">{role}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Date Added",
      dataIndex: "dateAdded",
      key: "dateAdded",
      width: 120,
    },
    ...(isCurrentUserFullAccess ? [{
      title: "Actions",
      key: "actions",
      width: 130,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", alignItems: "center" }}>
          <Tooltip title="Change Role">
            <EditOutlined
              style={{ color: "#1890ff", fontSize: 16, cursor: "pointer" }}
              onClick={() => handleOpenChangeRole(record)}
            />
          </Tooltip>
          <Tooltip title="Reset Password">
            <LockOutlined
              style={{ color: "#faad14", fontSize: 16, cursor: "pointer" }}
              onClick={() => handleOpenChangePassword(record)}
            />
          </Tooltip>
          {record.status !== "Active" && (
            <Tooltip title="Resend Invitation">
              <SendOutlined
                style={{ color: "#00d9a9", fontSize: 16, cursor: "pointer" }}
                onClick={() => handleResend(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    }] : []),
  ];

  const invitationColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 160,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (text) => <span style={{ color: "#014c75" }}>{text}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
      render: (role) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const colors = {
          pending: "orange",
          accepted: "green",
          expired: "red",
          cancelled: "default",
        };
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {getStatusIcon(status)}
            <Tag color={colors[status] || "default"}>{status}</Tag>
          </span>
        );
      },
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 120,
      render: (text, record) => (
        <span style={{ color: record.isExpired ? "#ff4d4f" : "#666" }}>
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", alignItems: "center" }}>
          {record.status === "pending" && !record.isExpired && (
            <Tooltip title="Resend Invitation">
              <SendOutlined
                style={{ color: "#00d9a9", fontSize: "16px", cursor: "pointer" }}
                onClick={() => handleResend(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ color: "#ff4d4f", fontSize: "16px", cursor: "pointer" }}
              onClick={() => handleDeleteInvitation(record)}
            />
          </Tooltip>
          {record.status === "pending" && (
            <Dropdown
              menu={{
                items: getActionMenuItems(record),
                onClick: ({ key }) => {
                  if (key === "cancel") {
                    handleCancelInvitation(record);
                  } else if (key === "delete") {
                    handleDeleteInvitation(record);
                  }
                },
              }}
              trigger={["click"]}
            >
              <MoreOutlined
                style={{ fontSize: "18px", cursor: "pointer", color: "#595959" }}
              />
            </Dropdown>
          )}
        </div>
      ),
    },
  ];

  const pendingCount = invitations.filter((i) => i.status === "pending").length;
  const acceptedCount = invitations.filter((i) => i.status === "accepted").length;

  const userRole = localStorage.getItem("userRole");
  const accountPath = userRole === "Admin"
    ? "/admin/account"
    : userRole === "Partner"
    ? "/partner/account"
    : "/customer/account";

  const noOrgEmptyText = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#014c75", margin: "12px 0 6px" }}>
            No organization connected yet
          </p>
          <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
            Connect your account to an organization to manage team members and send invitations.
          </p>
          <Button
            type="primary"
            size="small"
            style={{ background: "#014c75", borderColor: "#014c75" }}
            onClick={() => navigate(accountPath)}
          >
            Go to Account Settings
          </Button>
        </div>
      }
    />
  );

  const tabItems = [
    {
      key: "members",
      label: `Team Members (${members.length})`,
      children: (
        <Table
          columns={memberColumns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            pageSize: 10,
            showTotal: (total) => `Total ${total} members`,
          }}
          locale={{
            emptyText: noOrgFound
              ? noOrgEmptyText
              : <Empty description="No team members added yet" />,
          }}
        />
      ),
    },
    {
      key: "invitations",
      label: (
        <span>
          Pending Invitations
          {pendingCount > 0 && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              {pendingCount}
            </Tag>
          )}
        </span>
      ),
      children: (
        <Table
          columns={invitationColumns}
          dataSource={invitations}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            pageSize: 10,
            showTotal: (total) => `Total ${total} invitations`,
          }}
          locale={{
            emptyText: noOrgFound
              ? noOrgEmptyText
              : <Empty description="No pending invitations" />,
          }}
        />
      ),
    },
  ];

  if (orgLoading) {
    return (
      <InviteContainer>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Spin size="large" />
        </div>
      </InviteContainer>
    );
  }

  return (
    <InviteContainer>
      <div className="invite-header">
        <div className="stats">
          <div className="stat-item">
            <UserOutlined style={{ fontSize: 20, color: "#014c75" }} />
            <span className="stat-count">{members.length}</span>
            <span className="stat-label">Team Members</span>
          </div>
          <div className="stat-item">
            <ClockCircleOutlined style={{ fontSize: 20, color: "#faad14" }} />
            <span className="stat-count">{pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="invite-button"
          onClick={handleInviteUser}
          disabled={noOrgFound}
        >
          Invite User
        </Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {/* Change Role Modal */}
      <Modal
        title={`Change Role — ${selectedMember?.name || ""}`}
        open={changeRoleVisible}
        onOk={() => roleForm.submit()}
        onCancel={() => { setChangeRoleVisible(false); roleForm.resetFields(); }}
        okText="Update Role"
        okButtonProps={{ loading: actionLoading, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        <Form form={roleForm} layout="vertical" onFinish={handleSubmitChangeRole} style={{ marginTop: 16 }}>
          <Form.Item label="Role Template" name="profileRoleId" extra="Leave empty to grant full access.">
            <Select
              placeholder="Select role (leave empty for full access)"
              allowClear
              loading={rolesLoading}
              notFoundContent={rolesLoading ? <Spin size="small" /> : "No roles available"}
            >
              {availableRoles.map((r) => (
                <Option key={r.id} value={r.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{r.name}</span>
                    <Tag className={`role-tag ${r.scope}`} style={{ marginLeft: 8 }}>
                      {r.scope === "global" ? "Global" : "Organization"}
                    </Tag>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={`Reset Password — ${selectedMember?.name || ""}`}
        open={changePasswordVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => { setChangePasswordVisible(false); passwordForm.resetFields(); }}
        okText="Reset Password"
        okButtonProps={{ loading: actionLoading, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleSubmitChangePassword} style={{ marginTop: 16 }}>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter a new password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password placeholder="Enter new password (min 8 characters)" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm the password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Invite Team Member"
        open={inviteModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setInviteModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText="Send Invitation"
        okButtonProps={{
          loading: submitting,
          style: { background: "#00d9a9", borderColor: "#00d9a9" },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitInvite}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Full Name"
            name="name"
            rules={[{ required: true, message: "Please enter full name" }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            label="Mobile Number"
            name="contact"
          >
            <Input placeholder="Enter mobile number (optional)" />
          </Form.Item>

          <Form.Item
            label="Role Template"
            name="profileRoleId"
            extra="Select a role to assign specific permissions. Leave empty for default team member access."
          >
            <Select
              placeholder="Select role (optional)"
              allowClear
              loading={rolesLoading}
              notFoundContent={rolesLoading ? <Spin size="small" /> : "No roles available"}
            >
              {availableRoles.map((r) => (
                <Option key={r.id} value={r.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{r.name}</span>
                    <Tag className={`role-tag ${r.scope}`} style={{ marginLeft: 8 }}>
                      {r.scope === "global" ? "Global" : "Organization"}
                    </Tag>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ background: "#f6ffed", padding: 12, borderRadius: 6, marginTop: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#52c41a" }}>
              The invited user will receive an email with a link to set their password and join your organization.
            </p>
          </div>
        </Form>
      </Modal>
    </InviteContainer>
  );
};

export default InviteUsersTab;
